/**
 * Lead Management Hooks
 * React Query hooks for fetching and managing lead data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { supabase } from '@/lib/supabase'
import type {
  LeadStatus,
  LeadSource,
  LeadFilters,
  LeadSort,
  LeadWithRelations,
  LeadStage,
  LeadStats,
  LeadActivity,
  LeadFollowUp,
  CreateLeadRequest,
  UpdateLeadRequest,
  CreateActivityRequest,
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
} from '@limo/shared'

// Refresh interval for real-time-like updates
const REFRESH_INTERVAL = 30 * 1000

/**
 * Get the active account ID (handles impersonation)
 */
function useActiveAccountId(): string | null {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const activeAccount = isImpersonating ? impersonatedAccount : account
  return activeAccount?.id || null
}

/**
 * Hook to get current user's profile ID
 */
function useCurrentUserId(): string | null {
  const { profile } = useAuth()
  return profile?.id || null
}

// =============================================================================
// Lead Stages
// =============================================================================

/**
 * Fetch all lead stages for the account
 */
export function useLeadStages() {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['lead-stages', accountId],
    queryFn: async () => {
      if (!accountId) return []
      const { data, error } = await supabase
        .from('lead_stages')
        .select('*')
        .eq('account_id', accountId)
        .order('position', { ascending: true })

      if (error) throw error

      // Transform snake_case to camelCase
      return (data || []).map((stage: any) => ({
        id: stage.id,
        accountId: stage.account_id,
        name: stage.name,
        color: stage.color,
        position: stage.position,
        isDefault: stage.is_default,
        isWon: stage.is_won,
        isLost: stage.is_lost,
        createdAt: stage.created_at,
        updatedAt: stage.updated_at,
      })) as LeadStage[]
    },
    enabled: !!accountId,
    staleTime: 60 * 1000,
  })
}

// =============================================================================
// Lead List & Stats
// =============================================================================

/**
 * Fetch paginated leads with filters
 */
export function useLeads(
  filters: LeadFilters = {},
  sort: LeadSort = { field: 'created_at', direction: 'desc' },
  page: number = 1,
  pageSize: number = 25
) {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['leads', accountId, filters, sort, page, pageSize],
    queryFn: async () => {
      if (!accountId) return { leads: [], total: 0, page: 1, pageSize, totalPages: 0 }

      // Build the query
      let query = supabase
        .from('leads')
        .select(`
          *,
          lead_stages!inner (
            id,
            name,
            color,
            position,
            is_default,
            is_won,
            is_lost
          ),
          profiles:assigned_to (
            id,
            full_name,
            email
          )
        `, { count: 'exact' })
        .eq('account_id', accountId)

      // Apply filters
      if (filters.status && filters.status.length > 0) {
        query = query.in('status', filters.status)
      }
      if (filters.stageIds && filters.stageIds.length > 0) {
        query = query.in('stage_id', filters.stageIds)
      }
      if (filters.sources && filters.sources.length > 0) {
        query = query.in('source', filters.sources)
      }
      if (filters.assignedTo) {
        if (filters.assignedTo === 'unassigned') {
          query = query.is('assigned_to', null)
        } else {
          query = query.eq('assigned_to', filters.assignedTo)
        }
      }
      if (filters.scoreMin !== undefined) {
        query = query.gte('score', filters.scoreMin)
      }
      if (filters.scoreMax !== undefined) {
        query = query.lte('score', filters.scoreMax)
      }
      if (filters.createdAfter) {
        query = query.gte('created_at', filters.createdAfter)
      }
      if (filters.createdBefore) {
        query = query.lte('created_at', filters.createdBefore)
      }
      if (filters.search) {
        const searchTerm = `%${filters.search}%`
        query = query.or(`customer_name.ilike.${searchTerm},customer_email.ilike.${searchTerm},customer_phone.ilike.${searchTerm},company_name.ilike.${searchTerm}`)
      }

      // Apply sorting
      const ascending = sort.direction === 'asc'
      query = query.order(sort.field, { ascending })

      // Apply pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      // Transform data to match our interface
      const leads: LeadWithRelations[] = (data || []).map((lead: any) => ({
        id: lead.id,
        accountId: lead.account_id,
        quoteId: lead.quote_id,
        stageId: lead.stage_id,
        source: lead.source,
        sourceDetail: lead.source_detail,
        customerName: lead.customer_name,
        customerEmail: lead.customer_email,
        customerPhone: lead.customer_phone,
        companyName: lead.company_name,
        serviceType: lead.service_type,
        pickupAddress: lead.pickup_address,
        pickupDatetime: lead.pickup_datetime,
        dropoffAddress: lead.dropoff_address,
        estimatedValue: lead.estimated_value,
        score: lead.score,
        scoreBreakdown: lead.score_breakdown,
        assignedTo: lead.assigned_to,
        assignedAt: lead.assigned_at,
        status: lead.status,
        lostReason: lead.lost_reason,
        outcomeNotes: lead.outcome_notes,
        nextFollowUpAt: lead.next_follow_up_at,
        lastContactedAt: lead.last_contacted_at,
        firstContactedAt: lead.first_contacted_at,
        contactCount: lead.contact_count,
        bookingId: lead.booking_id,
        convertedAt: lead.converted_at,
        tags: lead.tags || [],
        notes: lead.notes,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at,
        stage: lead.lead_stages ? {
          id: lead.lead_stages.id,
          accountId: lead.account_id,
          name: lead.lead_stages.name,
          color: lead.lead_stages.color,
          position: lead.lead_stages.position,
          isDefault: lead.lead_stages.is_default,
          isWon: lead.lead_stages.is_won,
          isLost: lead.lead_stages.is_lost,
          createdAt: '',
          updatedAt: '',
        } : undefined,
        assignee: lead.profiles ? {
          id: lead.profiles.id,
          fullName: lead.profiles.full_name,
          email: lead.profiles.email,
        } : undefined,
      }))

      const total = count || 0
      return {
        leads,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
      }
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 15 * 1000,
  })
}

/**
 * Fetch lead statistics
 */
export function useLeadStats() {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['lead-stats', accountId],
    queryFn: async (): Promise<LeadStats | null> => {
      if (!accountId) return null

      // Fetch counts by status
      const { data: statusData, error: statusError } = await supabase
        .from('leads')
        .select('status')
        .eq('account_id', accountId)

      if (statusError) throw statusError

      // Calculate stats
      const statusCounts = {
        open: 0,
        won: 0,
        lost: 0,
        archived: 0,
      }
      let totalScore = 0
      statusData?.forEach((lead) => {
        statusCounts[lead.status as LeadStatus]++
      })

      // Fetch this week's leads
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      const { count: thisWeekCount } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId)
        .gte('created_at', weekAgo.toISOString())

      // Fetch average score
      const { data: scoreData } = await supabase
        .from('leads')
        .select('score')
        .eq('account_id', accountId)
        .eq('status', 'open')

      const avgScore = scoreData && scoreData.length > 0
        ? Math.round(scoreData.reduce((sum, l) => sum + l.score, 0) / scoreData.length)
        : 0

      // Fetch follow-ups due
      const { count: followUpsDue } = await supabase
        .from('lead_follow_ups')
        .select('*, leads!inner(account_id)', { count: 'exact', head: true })
        .eq('leads.account_id', accountId)
        .eq('status', 'pending')
        .lte('due_at', new Date().toISOString())

      // Fetch by stage
      const { data: stageData } = await supabase
        .from('leads')
        .select('stage_id, lead_stages(name, color)')
        .eq('account_id', accountId)
        .neq('status', 'archived')

      const stageCounts: Record<string, { name: string; color: string; count: number }> = {}
      stageData?.forEach((lead: any) => {
        const stageId = lead.stage_id
        if (!stageCounts[stageId]) {
          stageCounts[stageId] = {
            name: lead.lead_stages?.name || 'Unknown',
            color: lead.lead_stages?.color || '#6b7280',
            count: 0,
          }
        }
        stageCounts[stageId].count++
      })

      // Fetch by source
      const { data: sourceData } = await supabase
        .from('leads')
        .select('source')
        .eq('account_id', accountId)

      const sourceCounts: Record<LeadSource, number> = {
        website: 0,
        phone: 0,
        email: 0,
        referral: 0,
        walk_in: 0,
        advertising: 0,
        other: 0,
      }
      sourceData?.forEach((lead) => {
        sourceCounts[lead.source as LeadSource]++
      })

      const total = statusData?.length || 0
      const wonPlusLost = statusCounts.won + statusCounts.lost
      const conversionRate = wonPlusLost > 0 ? (statusCounts.won / wonPlusLost) * 100 : 0

      return {
        total,
        open: statusCounts.open,
        won: statusCounts.won,
        lost: statusCounts.lost,
        archived: statusCounts.archived,
        thisWeek: thisWeekCount || 0,
        conversionRate: Math.round(conversionRate * 10) / 10,
        averageScore: avgScore,
        followUpsDue: followUpsDue || 0,
        byStage: Object.entries(stageCounts).map(([stageId, data]) => ({
          stageId,
          stageName: data.name,
          stageColor: data.color,
          count: data.count,
        })),
        bySource: Object.entries(sourceCounts)
          .filter(([, count]) => count > 0)
          .map(([source, count]) => ({
            source: source as LeadSource,
            count,
          })),
      }
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Single Lead
// =============================================================================

/**
 * Fetch a single lead by ID
 */
export function useLead(leadId: string | undefined) {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      if (!leadId) return null

      const { data, error } = await supabase
        .from('leads')
        .select(`
          *,
          lead_stages (
            id,
            name,
            color,
            position,
            is_default,
            is_won,
            is_lost
          ),
          profiles:assigned_to (
            id,
            full_name,
            email
          ),
          booking_quotes:quote_id (
            id,
            token,
            status,
            total_amount
          )
        `)
        .eq('id', leadId)
        .single()

      if (error) throw error

      // Transform to interface
      return {
        id: data.id,
        accountId: data.account_id,
        quoteId: data.quote_id,
        stageId: data.stage_id,
        source: data.source,
        sourceDetail: data.source_detail,
        customerName: data.customer_name,
        customerEmail: data.customer_email,
        customerPhone: data.customer_phone,
        companyName: data.company_name,
        serviceType: data.service_type,
        pickupAddress: data.pickup_address,
        pickupDatetime: data.pickup_datetime,
        dropoffAddress: data.dropoff_address,
        estimatedValue: data.estimated_value,
        score: data.score,
        scoreBreakdown: data.score_breakdown,
        assignedTo: data.assigned_to,
        assignedAt: data.assigned_at,
        status: data.status,
        lostReason: data.lost_reason,
        outcomeNotes: data.outcome_notes,
        nextFollowUpAt: data.next_follow_up_at,
        lastContactedAt: data.last_contacted_at,
        firstContactedAt: data.first_contacted_at,
        contactCount: data.contact_count,
        bookingId: data.booking_id,
        convertedAt: data.converted_at,
        tags: data.tags || [],
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        stage: data.lead_stages ? {
          id: data.lead_stages.id,
          accountId: data.account_id,
          name: data.lead_stages.name,
          color: data.lead_stages.color,
          position: data.lead_stages.position,
          isDefault: data.lead_stages.is_default,
          isWon: data.lead_stages.is_won,
          isLost: data.lead_stages.is_lost,
          createdAt: '',
          updatedAt: '',
        } : undefined,
        assignee: data.profiles ? {
          id: data.profiles.id,
          fullName: data.profiles.full_name,
          email: data.profiles.email,
        } : undefined,
        quote: data.booking_quotes ? {
          id: data.booking_quotes.id,
          token: data.booking_quotes.token,
          status: data.booking_quotes.status,
          totalAmount: data.booking_quotes.total_amount,
        } : undefined,
      } as LeadWithRelations & { quote?: { id: string; token: string; status: string; totalAmount: number } }
    },
    enabled: !!leadId && !!accountId,
    staleTime: 10 * 1000,
  })
}

// =============================================================================
// Lead Mutations
// =============================================================================

/**
 * Create a new lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async (request: CreateLeadRequest) => {
      if (!accountId) throw new Error('No account ID')

      // Get default stage
      const { data: stages } = await supabase
        .from('lead_stages')
        .select('id')
        .eq('account_id', accountId)
        .eq('is_default', true)
        .single()

      if (!stages) throw new Error('No default stage found')

      const { data, error } = await supabase
        .from('leads')
        .insert({
          account_id: accountId,
          stage_id: stages.id,
          source: request.source,
          source_detail: request.sourceDetail,
          customer_name: request.customerName,
          customer_email: request.customerEmail,
          customer_phone: request.customerPhone,
          company_name: request.companyName,
          service_type: request.serviceType,
          pickup_address: request.pickupAddress,
          pickup_datetime: request.pickupDatetime,
          dropoff_address: request.dropoffAddress,
          estimated_value: request.estimatedValue,
          notes: request.notes,
          tags: request.tags || [],
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats', accountId] })
    },
  })
}

/**
 * Update a lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async ({ leadId, ...request }: UpdateLeadRequest & { leadId: string }) => {
      const updateData: Record<string, any> = {}

      if (request.stageId !== undefined) updateData.stage_id = request.stageId
      if (request.source !== undefined) updateData.source = request.source
      if (request.sourceDetail !== undefined) updateData.source_detail = request.sourceDetail
      if (request.customerName !== undefined) updateData.customer_name = request.customerName
      if (request.customerEmail !== undefined) updateData.customer_email = request.customerEmail
      if (request.customerPhone !== undefined) updateData.customer_phone = request.customerPhone
      if (request.companyName !== undefined) updateData.company_name = request.companyName
      if (request.serviceType !== undefined) updateData.service_type = request.serviceType
      if (request.pickupAddress !== undefined) updateData.pickup_address = request.pickupAddress
      if (request.pickupDatetime !== undefined) updateData.pickup_datetime = request.pickupDatetime
      if (request.dropoffAddress !== undefined) updateData.dropoff_address = request.dropoffAddress
      if (request.estimatedValue !== undefined) updateData.estimated_value = request.estimatedValue
      if (request.assignedTo !== undefined) {
        updateData.assigned_to = request.assignedTo
        if (request.assignedTo) {
          updateData.assigned_at = new Date().toISOString()
        } else {
          updateData.assigned_at = null
        }
      }
      if (request.status !== undefined) updateData.status = request.status
      if (request.lostReason !== undefined) updateData.lost_reason = request.lostReason
      if (request.outcomeNotes !== undefined) updateData.outcome_notes = request.outcomeNotes
      if (request.notes !== undefined) updateData.notes = request.notes
      if (request.tags !== undefined) updateData.tags = request.tags

      const { data, error } = await supabase
        .from('leads')
        .update(updateData)
        .eq('id', leadId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats', accountId] })
    },
  })
}

// =============================================================================
// Activities
// =============================================================================

/**
 * Fetch activities for a lead
 */
export function useLeadActivities(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-activities', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_activities')
        .select(`
          *,
          profiles:performed_by (
            id,
            full_name,
            email
          )
        `)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map((activity: any) => ({
        id: activity.id,
        leadId: activity.lead_id,
        activityType: activity.activity_type,
        title: activity.title,
        description: activity.description,
        oldValue: activity.old_value,
        newValue: activity.new_value,
        performedBy: activity.performed_by,
        performer: activity.profiles ? {
          id: activity.profiles.id,
          fullName: activity.profiles.full_name,
          email: activity.profiles.email,
        } : undefined,
        createdAt: activity.created_at,
      })) as LeadActivity[]
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
  })
}

/**
 * Create a new activity
 * After logging a call, email, or meeting activity, auto-moves leads from
 * "New Lead" stage to either "Contacted" or "Follow Up" depending on whether
 * the lead has active pending follow-ups.
 */
export function useCreateActivity() {
  const queryClient = useQueryClient()
  const userId = useCurrentUserId()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async (request: CreateActivityRequest) => {
      const { data, error } = await supabase
        .from('lead_activities')
        .insert({
          lead_id: request.leadId,
          activity_type: request.activityType,
          title: request.title,
          description: request.description,
          performed_by: userId,
        })
        .select()
        .single()

      if (error) throw error

      // Update last_contacted_at and contact_count if this is a contact activity
      if (['call', 'email', 'meeting'].includes(request.activityType)) {
        await supabase
          .from('leads')
          .update({
            last_contacted_at: new Date().toISOString(),
            contact_count: supabase.rpc('increment_contact_count', { lead_id: request.leadId }),
          })
          .eq('id', request.leadId)

        // Auto-stage-change: move to "Contacted" or "Follow Up" from any non-terminal stage
        try {
          // 1. Get current lead with stage info
          const { data: currentLead } = await supabase
            .from('leads')
            .select('id, stage_id, status, account_id, lead_stages!inner(name, is_won, is_lost)')
            .eq('id', request.leadId)
            .single()

          const currentStage = (currentLead as any)?.lead_stages
          const currentStageName = currentStage?.name

          // 2. Skip if not open, already in Contacted, or in terminal stage (Won/Lost)
          if (
            currentLead?.status !== 'open' ||
            currentStageName === 'Contacted' ||
            currentStage?.is_won ||
            currentStage?.is_lost
          ) {
            // No auto-move needed
          } else {
            // 3. Check for active pending follow-ups
            const { count: pendingFollowUps } = await supabase
              .from('lead_follow_ups')
              .select('*', { count: 'exact', head: true })
              .eq('lead_id', request.leadId)
              .eq('status', 'pending')

            // 4. If pending follow-ups exist and already in Follow Up, stay locked
            if (pendingFollowUps && pendingFollowUps > 0 && currentStageName === 'Follow Up') {
              // Stage is locked -- do nothing
            } else {
              // 5. Determine target stage
              const targetStageName = (pendingFollowUps && pendingFollowUps > 0)
                ? 'Follow Up'
                : 'Contacted'

              const leadAccountId = currentLead?.account_id || accountId
              const { data: targetStage } = await supabase
                .from('lead_stages')
                .select('id')
                .eq('account_id', leadAccountId)
                .eq('name', targetStageName)
                .single()

              if (targetStage) {
                await supabase
                  .from('leads')
                  .update({ stage_id: targetStage.id })
                  .eq('id', request.leadId)
              }
            }
          }
        } catch {
          // Silently ignore auto-stage errors -- the activity was already logged
          // This prevents blocking the main action if stage lookup fails
        }
      }

      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['enhanced-lead-stats'] })
    },
  })
}

// =============================================================================
// Follow-ups
// =============================================================================

/**
 * Fetch follow-ups for a lead
 */
export function useLeadFollowUps(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-follow-ups', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_follow_ups')
        .select(`
          *,
          profiles:assigned_to (
            id,
            full_name,
            email
          )
        `)
        .eq('lead_id', leadId)
        .order('due_at', { ascending: true })

      if (error) throw error

      return (data || []).map((followUp: any) => ({
        id: followUp.id,
        leadId: followUp.lead_id,
        title: followUp.title,
        description: followUp.description,
        dueAt: followUp.due_at,
        reminderAt: followUp.reminder_at,
        reminderSent: followUp.reminder_sent,
        assignedTo: followUp.assigned_to,
        assignee: followUp.profiles ? {
          id: followUp.profiles.id,
          fullName: followUp.profiles.full_name,
          email: followUp.profiles.email,
        } : undefined,
        status: followUp.status,
        completedAt: followUp.completed_at,
        completedBy: followUp.completed_by,
        createdAt: followUp.created_at,
        updatedAt: followUp.updated_at,
      })) as LeadFollowUp[]
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
  })
}

/**
 * Create a follow-up
 */
export function useCreateFollowUp() {
  const queryClient = useQueryClient()
  const userId = useCurrentUserId()

  return useMutation({
    mutationFn: async (request: CreateFollowUpRequest) => {
      const { data, error } = await supabase
        .from('lead_follow_ups')
        .insert({
          lead_id: request.leadId,
          title: request.title,
          description: request.description,
          due_at: request.dueAt,
          reminder_at: request.reminderAt,
          assigned_to: request.assignedTo || userId,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['leads-needing-attention'] })
    },
  })
}

/**
 * Update a follow-up
 */
export function useUpdateFollowUp() {
  const queryClient = useQueryClient()
  const userId = useCurrentUserId()

  return useMutation({
    mutationFn: async ({ followUpId, leadId, ...request }: UpdateFollowUpRequest & { followUpId: string; leadId: string }) => {
      const updateData: Record<string, any> = {}

      if (request.title !== undefined) updateData.title = request.title
      if (request.description !== undefined) updateData.description = request.description
      if (request.dueAt !== undefined) updateData.due_at = request.dueAt
      if (request.reminderAt !== undefined) updateData.reminder_at = request.reminderAt
      if (request.assignedTo !== undefined) updateData.assigned_to = request.assignedTo
      if (request.status !== undefined) {
        updateData.status = request.status
        if (request.status === 'completed') {
          updateData.completed_at = new Date().toISOString()
          updateData.completed_by = userId
        }
      }

      const { data, error } = await supabase
        .from('lead_follow_ups')
        .update(updateData)
        .eq('id', followUpId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['leads-needing-attention'] })
    },
  })
}

// =============================================================================
// Upcoming Follow-Ups (Cross-Lead)
// =============================================================================

/**
 * Follow-up item with associated lead data for cross-lead display
 */
export interface UpcomingFollowUpItem {
  id: string
  leadId: string
  title: string
  description?: string
  dueAt: string
  status: string
  assignedTo: string
  leadName: string
  leadEmail?: string
  leadPhone?: string
  leadScore: number
  leadStageName: string
  leadStageColor: string
  leadStatus: string
}

/**
 * Fetch all pending follow-ups across all leads for the active account
 * Used by the "Follow Up" tab on the leads page
 */
export function useUpcomingFollowUps(
  filter: 'all' | 'overdue' | 'today' | 'upcoming' = 'all',
  page: number = 1,
  pageSize: number = 25
) {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['upcoming-follow-ups', accountId, filter, page, pageSize],
    queryFn: async () => {
      if (!accountId) return { followUps: [], total: 0, page: 1, pageSize, totalPages: 0, overdueCount: 0, todayCount: 0, upcomingCount: 0, totalPending: 0 }

      const now = new Date()
      const todayStart = new Date(now)
      todayStart.setHours(0, 0, 0, 0)
      const todayEnd = new Date(now)
      todayEnd.setHours(23, 59, 59, 999)

      // Build main query
      let query = supabase
        .from('lead_follow_ups')
        .select(`
          id,
          lead_id,
          title,
          description,
          due_at,
          status,
          assigned_to,
          leads!inner (
            id,
            account_id,
            customer_name,
            customer_email,
            customer_phone,
            score,
            status,
            lead_stages!inner (
              name,
              color
            )
          )
        `, { count: 'exact' })
        .eq('leads.account_id', accountId)
        .eq('status', 'pending')
        .in('leads.status', ['open']) // Only show follow-ups for open leads

      // Apply time filter
      if (filter === 'overdue') {
        query = query.lt('due_at', now.toISOString())
      } else if (filter === 'today') {
        query = query.gte('due_at', todayStart.toISOString()).lte('due_at', todayEnd.toISOString())
      } else if (filter === 'upcoming') {
        query = query.gt('due_at', todayEnd.toISOString())
      }

      // Sort by due_at ascending (soonest first)
      query = query.order('due_at', { ascending: true })

      // Apply pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      // Get counts for sub-filter badges
      const [
        { count: overdueCount },
        { count: todayCount },
        { count: upcomingCount },
        { count: totalPending },
      ] = await Promise.all([
        supabase
          .from('lead_follow_ups')
          .select('*, leads!inner(account_id, status)', { count: 'exact', head: true })
          .eq('leads.account_id', accountId)
          .eq('status', 'pending')
          .in('leads.status', ['open'])
          .lt('due_at', now.toISOString()),
        supabase
          .from('lead_follow_ups')
          .select('*, leads!inner(account_id, status)', { count: 'exact', head: true })
          .eq('leads.account_id', accountId)
          .eq('status', 'pending')
          .in('leads.status', ['open'])
          .gte('due_at', todayStart.toISOString())
          .lte('due_at', todayEnd.toISOString()),
        supabase
          .from('lead_follow_ups')
          .select('*, leads!inner(account_id, status)', { count: 'exact', head: true })
          .eq('leads.account_id', accountId)
          .eq('status', 'pending')
          .in('leads.status', ['open'])
          .gt('due_at', todayEnd.toISOString()),
        supabase
          .from('lead_follow_ups')
          .select('*, leads!inner(account_id, status)', { count: 'exact', head: true })
          .eq('leads.account_id', accountId)
          .eq('status', 'pending')
          .in('leads.status', ['open']),
      ])

      // Transform data
      const followUps: UpcomingFollowUpItem[] = (data || []).map((item: any) => ({
        id: item.id,
        leadId: item.lead_id,
        title: item.title,
        description: item.description,
        dueAt: item.due_at,
        status: item.status,
        assignedTo: item.assigned_to,
        leadName: item.leads?.customer_name || 'Unknown',
        leadEmail: item.leads?.customer_email,
        leadPhone: item.leads?.customer_phone,
        leadScore: item.leads?.score || 0,
        leadStageName: item.leads?.lead_stages?.name || 'Unknown',
        leadStageColor: item.leads?.lead_stages?.color || '#6b7280',
        leadStatus: item.leads?.status || 'open',
      }))

      const total = count || 0
      return {
        followUps,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        overdueCount: overdueCount || 0,
        todayCount: todayCount || 0,
        upcomingCount: upcomingCount || 0,
        totalPending: totalPending || 0,
      }
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 15 * 1000,
  })
}

// =============================================================================
// Speed to Lead Metrics
// =============================================================================

/**
 * Fetch speed to lead metrics
 */
export function useSpeedToLeadMetrics() {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['speed-metrics', accountId],
    queryFn: async () => {
      if (!accountId) return { avgMinutes: null, medianMinutes: null, neverContacted: 0, contactedToday: 0, distribution: [] }

      // Get leads with first contact times
      const { data: contactedLeads } = await supabase
        .from('leads')
        .select('created_at, first_contacted_at')
        .eq('account_id', accountId)
        .not('first_contacted_at', 'is', null)

      // Get count of never contacted open leads
      const { count: neverContacted } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId)
        .eq('status', 'open')
        .is('first_contacted_at', null)

      // Get contacted today count
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const { count: contactedToday } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId)
        .gte('first_contacted_at', today.toISOString())

      if (!contactedLeads || contactedLeads.length === 0) {
        return {
          avgMinutes: null,
          medianMinutes: null,
          neverContacted: neverContacted || 0,
          contactedToday: contactedToday || 0,
          distribution: [],
        }
      }

      // Calculate response times in minutes
      const responseTimes = contactedLeads.map((lead) => {
        const created = new Date(lead.created_at).getTime()
        const contacted = new Date(lead.first_contacted_at!).getTime()
        return (contacted - created) / 60000 // minutes
      }).filter(t => t >= 0) // Filter out negative times (data issues)

      if (responseTimes.length === 0) {
        return {
          avgMinutes: null,
          medianMinutes: null,
          neverContacted: neverContacted || 0,
          contactedToday: contactedToday || 0,
          distribution: [],
        }
      }

      // Calculate average
      const avgMinutes = Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)

      // Calculate median
      const sorted = [...responseTimes].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      const medianMinutes = Math.round(
        sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
      )

      // Calculate distribution
      const ranges = [
        { range: '< 5 min', max: 5 },
        { range: '5-15 min', max: 15 },
        { range: '15-30 min', max: 30 },
        { range: '30-60 min', max: 60 },
        { range: '1-4 hr', max: 240 },
        { range: '4-24 hr', max: 1440 },
        { range: '> 24 hr', max: Infinity },
      ]

      const distribution = ranges.map((r, i) => {
        const min = i === 0 ? 0 : ranges[i - 1].max
        const count = responseTimes.filter((t) => t >= min && t < r.max).length
        return {
          range: r.range,
          count,
          percentage: Math.round((count / responseTimes.length) * 100),
        }
      })

      return {
        avgMinutes,
        medianMinutes,
        neverContacted: neverContacted || 0,
        contactedToday: contactedToday || 0,
        distribution,
      }
    },
    enabled: !!accountId,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  })
}

// =============================================================================
// Leads Needing Attention
// =============================================================================

/**
 * Fetch leads needing attention (awaiting contact > 30 min, overdue follow-ups)
 */
export function useLeadsNeedingAttention() {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['leads-needing-attention', accountId],
    queryFn: async () => {
      if (!accountId) return { awaitingContact: [], overdueFollowUps: [], totalCount: 0 }

      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString()
      const now = new Date().toISOString()

      // Leads waiting > 30 min for first contact
      const { data: awaitingContactData } = await supabase
        .from('leads')
        .select('id, customer_name, customer_email, customer_phone, created_at, score')
        .eq('account_id', accountId)
        .eq('status', 'open')
        .is('first_contacted_at', null)
        .lte('created_at', thirtyMinAgo)
        .order('score', { ascending: false })
        .limit(10)

      // Overdue follow-ups
      const { data: overdueFollowUpsData } = await supabase
        .from('lead_follow_ups')
        .select(`
          id,
          title,
          due_at,
          leads!inner (
            id,
            customer_name,
            customer_email,
            customer_phone,
            account_id,
            score,
            created_at
          )
        `)
        .eq('leads.account_id', accountId)
        .eq('status', 'pending')
        .lte('due_at', now)
        .limit(10)

      // Transform awaiting contact data
      const awaitingContact = (awaitingContactData || []).map((lead: any) => {
        const minutesWaiting = Math.floor(
          (Date.now() - new Date(lead.created_at).getTime()) / 60000
        )
        return {
          id: lead.id,
          customerName: lead.customer_name,
          customerEmail: lead.customer_email,
          customerPhone: lead.customer_phone,
          createdAt: lead.created_at,
          score: lead.score,
          reason: 'awaiting_contact' as const,
          minutesWaiting,
        }
      })

      // Transform overdue follow-ups data
      const overdueFollowUps = (overdueFollowUpsData || []).map((followUp: any) => ({
        id: followUp.leads.id,
        customerName: followUp.leads.customer_name,
        customerEmail: followUp.leads.customer_email,
        customerPhone: followUp.leads.customer_phone,
        createdAt: followUp.leads.created_at,
        score: followUp.leads.score,
        reason: 'overdue_followup' as const,
        followUpTitle: followUp.title,
        followUpDueAt: followUp.due_at,
      }))

      return {
        awaitingContact,
        overdueFollowUps,
        totalCount: awaitingContact.length + overdueFollowUps.length,
      }
    },
    enabled: !!accountId,
    refetchInterval: 60 * 1000, // Check every minute
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Enhanced Lead Stats (Sales-focused)
// =============================================================================

/**
 * Fetch enhanced lead stats with sales focus
 */
export function useEnhancedLeadStats() {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['enhanced-lead-stats', accountId],
    queryFn: async () => {
      if (!accountId) return null

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Get base stats
      const { data: allLeads } = await supabase
        .from('leads')
        .select('status, score, created_at, first_contacted_at')
        .eq('account_id', accountId)

      if (!allLeads) return null

      // Calculate stats
      const statusCounts = { open: 0, won: 0, lost: 0, archived: 0 }
      let totalScore = 0
      let newToday = 0
      let awaitingContact = 0
      let hotLeads = 0
      let contactedWithTime = 0
      let totalResponseMinutes = 0

      allLeads.forEach((lead) => {
        statusCounts[lead.status as LeadStatus]++

        // New today
        if (new Date(lead.created_at) >= today) {
          newToday++
        }

        // Awaiting contact (open, no first_contacted_at)
        if (lead.status === 'open' && !lead.first_contacted_at) {
          awaitingContact++
        }

        // Hot leads (score >= 70, open)
        if (lead.status === 'open' && lead.score >= 70) {
          hotLeads++
        }

        // For average response time calculation
        if (lead.first_contacted_at) {
          const created = new Date(lead.created_at).getTime()
          const contacted = new Date(lead.first_contacted_at).getTime()
          const minutes = (contacted - created) / 60000
          if (minutes >= 0) {
            contactedWithTime++
            totalResponseMinutes += minutes
          }
        }

        if (lead.status === 'open') {
          totalScore += lead.score
        }
      })

      // Get follow-ups due
      const { count: followUpsDue } = await supabase
        .from('lead_follow_ups')
        .select('*, leads!inner(account_id)', { count: 'exact', head: true })
        .eq('leads.account_id', accountId)
        .eq('status', 'pending')
        .lte('due_at', new Date().toISOString())

      // This week count
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      const thisWeek = allLeads.filter(l => new Date(l.created_at) >= weekAgo).length

      const total = allLeads.length
      const wonPlusLost = statusCounts.won + statusCounts.lost
      const conversionRate = wonPlusLost > 0 ? Math.round((statusCounts.won / wonPlusLost) * 1000) / 10 : 0
      const averageScore = statusCounts.open > 0 ? Math.round(totalScore / statusCounts.open) : 0
      const avgResponseMinutes = contactedWithTime > 0 ? Math.round(totalResponseMinutes / contactedWithTime) : null

      return {
        total,
        open: statusCounts.open,
        won: statusCounts.won,
        lost: statusCounts.lost,
        archived: statusCounts.archived,
        thisWeek,
        conversionRate,
        averageScore,
        followUpsDue: followUpsDue || 0,
        // Enhanced stats
        newToday,
        awaitingContact,
        hotLeads,
        avgResponseMinutes,
        byStage: [], // Not needed for stats cards
        bySource: [], // Not needed for stats cards
      }
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Archive Query (Won/Lost leads)
// =============================================================================

/**
 * Fetch archived leads (won + lost)
 */
export function useArchivedLeads(
  outcomeFilter: 'all' | 'won' | 'lost' = 'all',
  sort: LeadSort = { field: 'updated_at', direction: 'desc' },
  page: number = 1,
  pageSize: number = 25
) {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: ['archived-leads', accountId, outcomeFilter, sort, page, pageSize],
    queryFn: async () => {
      if (!accountId) return { leads: [], total: 0, page: 1, pageSize, totalPages: 0, wonCount: 0, lostCount: 0 }

      // Build the query
      let query = supabase
        .from('leads')
        .select(`
          *,
          lead_stages!inner (
            id,
            name,
            color,
            position,
            is_default,
            is_won,
            is_lost
          ),
          profiles:assigned_to (
            id,
            full_name,
            email
          )
        `, { count: 'exact' })
        .eq('account_id', accountId)

      // Filter by outcome
      if (outcomeFilter === 'won') {
        query = query.eq('status', 'won')
      } else if (outcomeFilter === 'lost') {
        query = query.eq('status', 'lost')
      } else {
        query = query.in('status', ['won', 'lost'])
      }

      // Apply sorting
      const ascending = sort.direction === 'asc'
      query = query.order(sort.field, { ascending })

      // Apply pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      // Get counts for tabs
      const { count: wonCount } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId)
        .eq('status', 'won')

      const { count: lostCount } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId)
        .eq('status', 'lost')

      // Transform data
      const leads: LeadWithRelations[] = (data || []).map((lead: any) => ({
        id: lead.id,
        accountId: lead.account_id,
        quoteId: lead.quote_id,
        stageId: lead.stage_id,
        source: lead.source,
        sourceDetail: lead.source_detail,
        customerName: lead.customer_name,
        customerEmail: lead.customer_email,
        customerPhone: lead.customer_phone,
        companyName: lead.company_name,
        serviceType: lead.service_type,
        pickupAddress: lead.pickup_address,
        pickupDatetime: lead.pickup_datetime,
        dropoffAddress: lead.dropoff_address,
        estimatedValue: lead.estimated_value,
        score: lead.score,
        scoreBreakdown: lead.score_breakdown,
        assignedTo: lead.assigned_to,
        assignedAt: lead.assigned_at,
        status: lead.status,
        lostReason: lead.lost_reason,
        outcomeNotes: lead.outcome_notes,
        nextFollowUpAt: lead.next_follow_up_at,
        lastContactedAt: lead.last_contacted_at,
        firstContactedAt: lead.first_contacted_at,
        contactCount: lead.contact_count,
        bookingId: lead.booking_id,
        convertedAt: lead.converted_at,
        tags: lead.tags || [],
        notes: lead.notes,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at,
        stage: lead.lead_stages ? {
          id: lead.lead_stages.id,
          accountId: lead.account_id,
          name: lead.lead_stages.name,
          color: lead.lead_stages.color,
          position: lead.lead_stages.position,
          isDefault: lead.lead_stages.is_default,
          isWon: lead.lead_stages.is_won,
          isLost: lead.lead_stages.is_lost,
          createdAt: '',
          updatedAt: '',
        } : undefined,
        assignee: lead.profiles ? {
          id: lead.profiles.id,
          fullName: lead.profiles.full_name,
          email: lead.profiles.email,
        } : undefined,
      }))

      const total = count || 0
      return {
        leads,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        wonCount: wonCount || 0,
        lostCount: lostCount || 0,
      }
    },
    enabled: !!accountId,
    staleTime: 15 * 1000,
  })
}

// =============================================================================
// Delete Lead
// =============================================================================

/**
 * Delete a lead and all related data (activities, follow-ups cascade)
 */
export function useDeleteLead() {
  const queryClient = useQueryClient()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async ({ leadId }: { leadId: string }) => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['enhanced-lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['leads-needing-attention'] })
    },
  })
}

// =============================================================================
// Delete Activities & Follow-ups
// =============================================================================

/**
 * Delete a lead activity
 */
export function useDeleteActivity() {
  const queryClient = useQueryClient()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async ({ activityId, leadId }: { activityId: string; leadId: string }) => {
      const { error } = await supabase
        .from('lead_activities')
        .delete()
        .eq('id', activityId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['enhanced-lead-stats'] })
    },
  })
}

/**
 * Delete a lead follow-up
 */
export function useDeleteFollowUp() {
  const queryClient = useQueryClient()
  const accountId = useActiveAccountId()

  return useMutation({
    mutationFn: async ({ followUpId, leadId }: { followUpId: string; leadId: string }) => {
      const { error } = await supabase
        .from('lead_follow_ups')
        .delete()
        .eq('id', followUpId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads', accountId] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['leads-needing-attention'] })
    },
  })
}
